import { useCallback } from 'react';
import { ApolloError, QueryResult, useQuery, useMutation } from '@apollo/client';
import GET_CUSTOM_TEACHING_ORDERS from '../../queries/custom-teaching-order/get-custom-teaching-orders-query';
import CREATE_CUSTOM_TEACHING_ORDER from '../../queries/custom-teaching-order/create-custom-teaching-order';
import UPDATE_CUSTOM_TEACHING_ORDER from '../../queries/custom-teaching-order/update-custom-teaching-order';
import GET_TEACHING_ORDERS from '../../queries/teaching-order/get-teaching-orders-query';
import {
  CustomTeachingOrder,
  TeachingOrder,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { RequireAtLeastOne } from '@monorepo/shared/src/types';
import { StoreTypename } from 'types';

export type TeachingOrderStoreState = {
  teachingOrders: any;
  error: ApolloError | undefined;
  isLoading: boolean;
  activeTeachingOrder: TeachingOrder | CustomTeachingOrder;
};
interface TeachingOrderUpdateObject {
  id: number;
  name?: string;
  characterIds?: any[];
}
export type TeachingOrderUpdate = RequireAtLeastOne<
  TeachingOrderUpdateObject,
  'name' | 'characterIds'
>;

export type TeachingOrderStoreDispatch = {
  createCustomTeachingOrder: (characterIds: any[]) => Promise<any>;
  updateCustomTeachingOrder: (data: TeachingOrderUpdate) => void;
  customOrderRefetch: () => void;
  teachingOrderRefetch: () => void;
};
export type TeachingOrderStore = [TeachingOrderStoreState, TeachingOrderStoreDispatch];

export const getDefaultCustomTeachingOrderState = (): { __typename: string } => ({
  __typename: StoreTypename.CUSTOM_TEACHING_ORDERS,
});

const useTeachingOrderStore = (): TeachingOrderStore => {
  const {
    data: customTeachingOrders,
    loading: customLoading,
    error: customError,
    refetch: customOrderRefetch,
  }: QueryResult<{ customTeachingOrders: any }> = useQuery(GET_CUSTOM_TEACHING_ORDERS);

  const {
    data: teachingOrders,
    loading,
    error,
    refetch: teachingOrderRefetch,
  }: QueryResult<{ teachingOrders: any }> = useQuery(GET_TEACHING_ORDERS);

  const allTeachingOrders = [
    ...(customTeachingOrders?.customTeachingOrders || []),
    ...(teachingOrders?.teachingOrders || []),
  ];

  const activeTeachingOrder = allTeachingOrders.find((teachingOrder) => teachingOrder.active);

  // this will happen if for some reason the custom teaching order is set to active
  // and the teaching order id does not match any of the user's custom teaching orders
  if (!activeTeachingOrder && !error) {
    teachingOrderRefetch();
  }

  const [createCustomTeachingOrder] = useMutation(CREATE_CUSTOM_TEACHING_ORDER);
  const [updateCustomTeachingOrder] = useMutation(UPDATE_CUSTOM_TEACHING_ORDER);

  const createTeachingOrder = useCallback(async (characterIds: any[]) => {
    try {
      const customOrder = await createCustomTeachingOrder({
        variables: { data: { characterIds } },
      });
      await customOrderRefetch();
      return customOrder?.data?.createCustomTeachingOrder;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const updateTeachingOrder = useCallback(async (data: TeachingOrderUpdate) => {
    try {
      await updateCustomTeachingOrder({
        variables: { data },
      });
      await customOrderRefetch();
    } catch (error) {
      console.log(error);
    }
  }, []);

  return [
    {
      teachingOrders: allTeachingOrders || [],
      error: error || customError,
      isLoading: loading && customLoading,
      activeTeachingOrder: activeTeachingOrder,
    },
    {
      customOrderRefetch,
      teachingOrderRefetch,
      createCustomTeachingOrder: createTeachingOrder,
      updateCustomTeachingOrder: updateTeachingOrder,
    },
  ];
};

export default useTeachingOrderStore;
